"use client";

import { Card } from "@/components/ui/card";
import {} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils";
import {
  Bed,
  Calendar,
  DollarSign,
  Home,
  MapPin,
  ShowerHead,
  Square,
} from "lucide-react";

interface PropertyContextCardProps {
  price: number;
  pricePerSqft: number;
  beds: number;
  baths: number;
  sqft: number;
  daysOnMarket?: number | string;
  className?: string;
  variant?: "default" | "compact" | "inline";
}

export function PropertyContextCard({
  price,
  pricePerSqft,
  beds,
  baths,
  sqft,
  daysOnMarket,
  className,
  variant = "default",
}: PropertyContextCardProps) {
  if (variant === "inline") {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center gap-3 text-sm text-muted-foreground py-2",
          className,
        )}
      >
        <div className="flex items-center gap-1.5">
          <DollarSign className="h-4 w-4" />
          <span className="font-semibold text-foreground">
            {formatCurrency(price)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Square className="h-4 w-4" />
          <span>
            {formatCurrency(pricePerSqft)}
            <span className="text-xs">/sqft</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Bed className="h-4 w-4" />
          <span>{beds} beds</span>
        </div>
        <div className="flex items-center gap-1.5">
          <ShowerHead className="h-4 w-4" />
          <span>{baths} baths</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Square className="h-4 w-4" />
          <span>{sqft.toLocaleString()} sqft</span>
        </div>
        {daysOnMarket && (
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            <span>{daysOnMarket} on market</span>
          </div>
        )}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <Card className={cn("p-4 bg-white border border-gray-200", className)}>
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-yellow-400 text-white p-1 rounded-full">
                <MapPin className="h-3.5 w-3.5" />
              </div>
              <h3 className="text-sm font-semibold">Subject Property</h3>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2">
          <div>
            <div className="text-xs text-gray-500">Price</div>
            <div className="flex items-center mt-1">
              <span className="font-semibold">{formatCurrency(price)}</span>
            </div>
          </div>

          <div>
            <div className="text-xs text-gray-500">Price/sqft</div>
            <div className="flex items-center mt-1">
              <span className="font-semibold">
                {formatCurrency(pricePerSqft)}
              </span>
            </div>
          </div>

          <div>
            <div className="text-xs text-gray-500">Layout</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-semibold">{beds} bd</span>
              <span className="text-gray-300">•</span>
              <span className="font-semibold">{baths} ba</span>
            </div>
          </div>

          {daysOnMarket && (
            <div>
              <div className="text-xs text-gray-500">Days on market</div>
              <div className="flex flex-col mt-1">
                <span className="font-semibold">{daysOnMarket} days</span>
                <span className="text-xs text-gray-400">
                  at time of analysis
                </span>
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // Default variant
  return (
    <Card className={cn("p-4 border-gray-100", className)}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Home className="h-5 w-5 text-gray-500" />
          <h3 className="font-semibold">Property Details</h3>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3">
        <div>
          <div className="text-sm text-gray-500 mb-1">Price</div>
          <div className="text-lg font-semibold">{formatCurrency(price)}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">Price/sqft</div>
          <div className="text-lg font-semibold">
            {formatCurrency(pricePerSqft)}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">Size</div>
          <div className="text-lg font-semibold">
            {sqft.toLocaleString()} sqft
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">Bedrooms</div>
          <div className="text-lg font-semibold">{beds}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">Bathrooms</div>
          <div className="text-lg font-semibold">{baths}</div>
        </div>
        {daysOnMarket && (
          <div>
            <div className="text-sm text-gray-500 mb-1">Days on Market</div>
            <div className="flex flex-col">
              <span className="text-lg font-semibold">{daysOnMarket}</span>
              <span className="text-xs text-gray-500">at time of analysis</span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
